import React from 'react';
import {
  TrendingUp,
  TrendingDown,
  Timeline,
  ShowChart,
} from '@mui/icons-material';
import { KeyLevels } from '@andywilliams/indicators';
import { getEventTypeLabel } from '../constants/eventTypes';

// keyLevel.* (DWLF-333): one event family whose two-way kinds carry their
// direction under event.keyLevel.
const KEY_LEVEL_PREFIX = `${KeyLevels.KEY_LEVEL_EVENT_PREFIX}.`;
export const isKeyLevelEvent = (eventType) => typeof eventType === 'string' && eventType.startsWith(KEY_LEVEL_PREFIX);
// null and '' are no price (Number() would make them 0).
const toPrice = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));
const keyLevelDirection = (event) => event?.keyLevel?.direction ?? event?.direction;

// One line for a keyLevel.* row in an event list (DWLF-350).
export const describeKeyLevelEvent = (event) => {
  const keyLevel = event?.keyLevel || {};
  const kind = keyLevel.kind || String(event?.eventType || '').slice(KEY_LEVEL_PREFIX.length);
  const direction = keyLevel.direction && keyLevel.direction !== 'neutral' ? ` (${keyLevel.direction})` : '';
  const price = toPrice(keyLevel.levelPrice ?? event?.price);
  return `Key level ${kind}${direction} at ${Number.isFinite(price) ? price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : 'unknown level'}`;
};

export const getEventIcon = (eventType) => {
  if (isKeyLevelEvent(eventType)) {
    return <ShowChart />;
  }
  switch (eventType) {
  case 'trendline_break':
    return <Timeline />;
  case 'trendline_break_bullish':
    return <TrendingUp />;
  case 'trendline_break_bearish':
    return <TrendingDown />;
  case 'trendline_breach':
    return <Timeline />;
  case 'trendline_breach_bullish':
    return <TrendingUp />;
  case 'trendline_breach_bearish':
    return <TrendingDown />;
  case 'ema.cross.above':
  case 'sma.cross.above':
  case 'sma.reclaimed':
  case 'bollinger.break.aboveUpper':
  case 'dss.cross.bullish':
  case 'dss.level.overbought':
    return <TrendingUp />;
  case 'ema.cross.below':
  case 'sma.cross.below':
  case 'sma.lost':
  case 'bollinger.break.belowLower':
  case 'dss.cross.bearish':
  case 'dss.level.oversold':
    return <TrendingDown />;
  case 'swing_high_formed':
  case 'swing_low_formed':
  case 'atr.regime.expansion':
  case 'atr.regime.contraction':
    return <ShowChart />;
  case 'higher_high':
  case 'higher_low':
  case 'swing_high_break':
  case 'swing_low_sweep':
  case 'cycle.low.higher_low':
  case 'cycle.high.higher_high':
  case 'cycle.low.sweep':
    return <TrendingUp />;
  case 'lower_high':
  case 'lower_low':
  case 'swing_low_break':
  case 'swing_high_sweep':
  case 'cycle.low.lower_low':
  case 'cycle.high.lower_high':
  case 'cycle.high.sweep':
    return <TrendingDown />;
  case 'ema_bullish_alignment':
  case 'ema_bullish_unalignment':
  case 'ema_bullish_cloud_hit':
  case 'ema_bullish_alignment_sustained':
    return <TrendingUp />;
  case 'ema_bearish_alignment':
  case 'ema_bearish_unalignment':
  case 'ema_bearish_cloud_hit':
  case 'ema_bearish_alignment_sustained':
    return <TrendingDown />;
  default:
    return <ShowChart />;
  }
};

export const getEventColor = (eventType, event = null) => {
  if (isKeyLevelEvent(eventType)) {
    const direction = keyLevelDirection(event);
    if (direction === 'bullish') return 'success';
    if (direction === 'bearish') return 'error';
    return 'info';
  }
  switch (eventType) {
  case 'higher_high':
  case 'higher_low':
  case 'swing_high_break':
  case 'swing_low_sweep':
  case 'cycle.low.higher_low':
  case 'cycle.high.higher_high':
  case 'cycle.low.sweep':
  case 'ema_bullish_alignment':
  case 'ema_bullish_cloud_hit':
  case 'ema_bullish_alignment_sustained':
    return 'success';
  case 'lower_high':
  case 'lower_low':
  case 'swing_low_break':
  case 'swing_high_sweep':
  case 'cycle.low.lower_low':
  case 'cycle.high.lower_high':
  case 'cycle.high.sweep':
  case 'ema_bearish_alignment':
  case 'ema_bearish_cloud_hit':
  case 'ema_bearish_alignment_sustained':
    return 'error';
  case 'trendline_break':
    // Support Break = red (error), Resistance Break = green (success)
    if (event && event.breakType === 'support_break') {
      return 'error';
    } else if (event && event.breakType === 'resistance_break') {
      return 'success';
    }
    return 'warning'; // fallback for trendline breaks without breakType
  case 'trendline_break_bullish':
    return 'success';
  case 'trendline_break_bearish':
    return 'error';
  case 'trendline_breach':
  case 'trendline_breach_bullish':
  case 'trendline_breach_bearish':
    return 'warning';
  case 'swing_high_formed':
  case 'swing_low_formed':
  case 'ema_bullish_unalignment':
  case 'ema_bearish_unalignment':
    return 'primary';
  case 'ema.cross.above':
  case 'sma.cross.above':
  case 'sma.reclaimed':
  case 'bollinger.break.aboveUpper':
  case 'dss.cross.bullish':
  case 'dss.level.overbought':
    return 'success';
  case 'ema.cross.below':
  case 'sma.cross.below':
  case 'sma.lost':
  case 'bollinger.break.belowLower':
  case 'dss.cross.bearish':
  case 'dss.level.oversold':
    return 'error';
  case 'atr.regime.expansion':
    return 'warning';
  case 'atr.regime.contraction':
    return 'info';
  default:
    return 'default';
  }
};

export const getEventLabel = (event) => {
  if (!event || !event.eventType) {
    return 'Event';
  }
  if (isKeyLevelEvent(event.eventType)) {
    const price = toPrice(event.keyLevel?.levelPrice ?? event.price);
    const at = Number.isFinite(price) ? ` @ ${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '';
    return `${getEventTypeLabel(event.eventType)}${at}`;
  }
  switch (event.eventType) {
  case 'trendline_break':
    if (event && event.breakType === 'support_break') {
      return 'Support Break';
    }
    if (event && event.breakType === 'resistance_break') {
      return 'Resistance Break';
    }
    return getEventTypeLabel(event.eventType);
  case 'trendline_break_bullish':
  case 'trendline_break_bearish':
    return getEventTypeLabel(event.eventType);
  case 'trendline_breach':
    return event.breakType === 'support_break' ? 'Support Breach' : 'Resistance Breach';
  case 'trendline_breach_bullish':
  case 'trendline_breach_bearish':
    return getEventTypeLabel(event.eventType);
  case 'swing_high_formed':
    return 'Swing High Formed';
  case 'swing_high_break':
    return 'Swing High Break';
  case 'swing_low_formed':
    return 'Swing Low Formed';
  case 'swing_low_break':
    return 'Swing Low Break';
  case 'swing_high_sweep':
    return 'Swing High Sweep';
  case 'swing_low_sweep':
    return 'Swing Low Sweep';
  case 'higher_high':
    return 'Higher High';
  case 'higher_low':
    return 'Higher Low';
  case 'lower_high':
    return 'Lower High';
  case 'lower_low':
    return 'Lower Low';
  case 'cycle.low.higher_low':
    return 'Higher Cycle Low';
  case 'cycle.low.lower_low':
    return 'Lower Cycle Low';
  case 'cycle.high.higher_high':
    return 'Higher Cycle High';
  case 'cycle.high.lower_high':
    return 'Lower Cycle High';
  case 'cycle.low.sweep':
    return 'Cycle Low Sweep';
  case 'cycle.high.sweep':
    return 'Cycle High Sweep';
  case 'ema_bullish_alignment':
    return 'EMA Bullish Alignment';
  case 'ema_bullish_unalignment':
    return 'EMA Bullish Unalignment';
  case 'ema_bullish_cloud_hit':
    return 'EMA Bullish Cloud Hit';
  case 'ema_bullish_alignment_sustained':
    return 'EMA Bullish Alignment Sustained';
  case 'ema_bearish_alignment':
    return 'EMA Bearish Alignment';
  case 'ema_bearish_unalignment':
    return 'EMA Bearish Unalignment';
  case 'ema_bearish_cloud_hit':
    return 'EMA Bearish Cloud Hit';
  case 'ema_bearish_alignment_sustained':
    return 'EMA Bearish Alignment Sustained';
  case 'ema.cross.above':
    return 'EMA Bullish Cross';
  case 'ema.cross.below':
    return 'EMA Bearish Cross';
  case 'sma.cross.above':
    return 'SMA Bullish Cross';
  case 'sma.cross.below':
    return 'SMA Bearish Cross';
  case 'sma.reclaimed':
    return 'SMA Reclaimed';
  case 'sma.lost':
    return 'SMA Lost';
  case 'bollinger.break.aboveUpper':
    return 'Bollinger Break Above Upper';
  case 'bollinger.break.belowLower':
    return 'Bollinger Break Below Lower';
  case 'atr.regime.expansion':
    return 'Volatility Expansion';
  case 'atr.regime.contraction':
    return 'Volatility Contraction';
  case 'dss.cross.bullish':
    return 'DSS Bullish Cross';
  case 'dss.cross.bearish':
    return 'DSS Bearish Cross';
  case 'dss.level.overbought':
    return 'DSS Entered Overbought';
  case 'dss.level.oversold':
    return 'DSS Entered Oversold';
  default:
    return getEventTypeLabel(event.eventType);
  }
};
